import { MobXProviderContext } from "mobx-react";
import { useContext, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useLocation, useNavigate } from "react-router-dom";
import { RouterAddress } from "../../../constants/RouterAddress";
import { ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { BinderShare } from "../../../models/binder";
import { appDataTableWrapperStyle, appSectionCardStyle, renderSurfaceHeader } from "../../../components/general/uiSurfaceStyles";
import HiringRequestDialog from "../../home/components/HiringRequestDialog";
import AdminResetControl from "../../../components/general/AdminResetControl";

const formatDateTime = (value?: string) => {
  if (!value) {
    return "Forever";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString("en-US", { timeZone: "America/Los_Angeles" });
};

const isActiveShare = (share: BinderShare) => {
  if (!share.expirationDate) {
    return true;
  }
  const date = new Date(share.expirationDate).getTime();
  return Number.isNaN(date) || date > Date.now();
};

const newestFirst = (left: any, right: any) => String(right.createdAt || right.sentAt || "").localeCompare(String(left.createdAt || left.sentAt || ""));

const getHiringRequestEmail = (request: any) => {
  const candidates = request?.viewerMode === "recipient"
    ? [request.requesterEmail, request.ownerEmail, request.senderEmail]
    : [request.recipientEmail, request.targetEmail];
  return candidates.find((value) => /^\S+@\S+\.\S+$/.test(String(value || "").trim())) || "Email unavailable";
};

const getAnswerDrafts = (request: any): Record<string, string> => {
  const rawAnswers = String(request?.questionnaireAnswers || "").trim();
  if (!rawAnswers) return {};
  try {
    const parsed = JSON.parse(rawAnswers);
    if (Array.isArray(parsed)) {
      return parsed.reduce((answers, item) => ({ ...answers, [String(item.questionId || item.question || "")]: String(item.answer || "") }), {});
    }
    if (parsed && typeof parsed === "object") return parsed;
  } catch {
    return { general: rawAnswers };
  }
  return { general: rawAnswers };
};

const SharedContentEmptyState = ({ type }: { type: "sharing" | "hiring" }) => {
  const sharing = type === "sharing";

  return (
    <>
      <div className="flex flex-column align-items-center text-center mt-3">
        <div className="font-semibold text-2xl text-900">
          {sharing ? "Nothing has been shared with you yet" : "No hiring requests yet"}
        </div>
        <div className="text-color-secondary mt-2" style={{ maxWidth: "44rem" }}>
          {sharing
            ? "Add a home and resident to create and share a Binder."
            : "Hiring requests will appear here when you send or receive one."}
        </div>
      </div>
      <div className="mt-3 text-color-secondary" style={{ maxWidth: "42rem", marginLeft: "auto", marginRight: "auto" }}>
        <ul className="mt-0 mb-0 pl-5" style={{ lineHeight: 1.8, textAlign: "left" }}>
          {sharing ? <>
            <li>Share selected care documents for quick review.</li>
            <li>Useful for coordinating care with providers, nurses, and staff.</li>
          </> : <>
            <li>Collect certifications and answers in one request.</li>
            <li>Helpful when hiring caregivers, nurses, or other care staff.</li>
          </>}
        </ul>
      </div>
    </>
  );
};

const SharedContentPage = () => {
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [shares, setShares] = useState<BinderShare[]>([]);
  const [requests, setRequests] = useState<any[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<any | null>(null);
  const [answerDrafts, setAnswerDrafts] = useState<Record<string, string>>({});
  const [showHiringRequestDialog, setShowHiringRequestDialog] = useState(false);
  const [showHiringIntakeDialog, setShowHiringIntakeDialog] = useState(false);
  const [hiringRequestsRefreshKey, setHiringRequestsRefreshKey] = useState(0);
  const [showNotifyOwnerConfirmation, setShowNotifyOwnerConfirmation] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [savingRequest, setSavingRequest] = useState(false);
  const [loading, setLoading] = useState(true);
  const canCreateHiringRequest = authStore?.getUserRoleType?.() === ROLE_TYPE_PROVIDER || authStore?.isAdmin?.();
  const mobile = useResponsive().isMobile;
  const sortedShares = [...shares].sort(newestFirst);
  const sortedRequests = [...requests].sort(newestFirst);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.listBinderSharesSharedWithMe();
        if (!cancelled) {
          setShares((response || []).filter(isActiveShare));
        }
        try {
          const sentRequests = await backendAPIStore.listHiringRequests();
          if (!cancelled) setRequests(sentRequests || []);
        } catch {
          if (!cancelled) setRequests([]);
        }
        const requestId = new URLSearchParams(location.search).get("requestId");
        if (requestId) {
          setLoadingRequest(true);
          try {
            const request = await backendAPIStore.getHiringRequest(requestId);
            if (!cancelled) {
              setSelectedRequest(request);
              setAnswerDrafts(getAnswerDrafts(request));
              setShowHiringRequestDialog(true);
            }
          } finally {
            if (!cancelled) setLoadingRequest(false);
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [backendAPIStore, hiringRequestsRefreshKey, location.search]);

  const saveAnswers = async (notifyOwner: boolean) => {
    if (!selectedRequest?.requestId) return;
    const answerItems = (selectedRequest.questions || []).map((item: any) => ({
      questionId: item.questionId,
      question: item.question,
      answer: String(answerDrafts[String(item.questionId || item.question || "")] || "").trim(),
    }));
    const answers = answerItems.length ? JSON.stringify(answerItems) : String(answerDrafts.general || "").trim();
    if (!answers.trim()) return;
    setSavingRequest(true);
    try {
      const updated = await backendAPIStore.updateHiringRequest({ requestId: selectedRequest.requestId, questionnaireAnswers: answers, status: "completed", notifyOwner });
      setSelectedRequest(updated);
      setRequests((current) => current.map((request) => request.requestId === updated.requestId ? updated : request));
      setShowHiringRequestDialog(false);
    } finally {
      setSavingRequest(false);
    }
  };

  const openHiringRequest = (request: any) => {
    setSelectedRequest(request);
    setAnswerDrafts(getAnswerDrafts(request));
    setShowHiringRequestDialog(true);
  };

  const confirmNotifyOwner = () => {
    setShowNotifyOwnerConfirmation(false);
    void saveAnswers(true);
  };

  const resetSharedContent = async () => {
    await backendAPIStore.resetSharedContent();
    setShares([]);
    setRequests([]);
  };

  return (
    <div className="m-3">
      <Card style={appSectionCardStyle}>
        {renderSurfaceHeader("Shared Content", "Binders shared with you in Hub.", "pi pi-share-alt")}
        {shares.length ? <div className="mt-3" style={appDataTableWrapperStyle}>
        {mobile ? <div className="flex flex-column gap-3">
          {sortedShares.map((share) => (
            <div key={share.shareId} className="surface-card border-1 surface-border border-round p-3">
              <div className="flex align-items-start justify-content-between gap-3">
                <div style={{ minWidth: 0 }}>
                  <div className="font-semibold text-lg">Binder</div>
                  <div className="text-color-secondary mt-1">{share.ownerDisplayName || share.ownerEmail || "Hub user"}</div>
                </div>
                <Tag value="Active" severity="success" />
              </div>
              <div className="grid mt-2 row-gap-2">
                <div className="col-6"><div className="text-color-secondary text-sm">Facility</div><div className="font-medium">{share.facilityType ? String(share.facilityType).replace(/_/g, " ").toUpperCase() : "Not specified"}</div></div>
                <div className="col-6"><div className="text-color-secondary text-sm">Folders</div><div className="font-medium">{`${share.folderIds?.length || "All shareable"}`}</div></div>
                <div className="col-12"><div className="text-color-secondary text-sm">Expires</div><div className="font-medium">{formatDateTime(share.expirationDate)}</div></div>
              </div>
              <Button label="Open Binder" icon="pi pi-folder-open" className="p-button-outlined w-full mt-3" onClick={() => share.shareId && navigate(RouterAddress.getSharedBinderPagePath(share.shareId))} />
            </div>
          ))}
        </div> : <DataTable value={sortedShares} loading={loading} paginator={shares.length > 5} rows={5} emptyMessage="No active Binders have been shared with you.">
            <Column header="Binder" body={() => "Binder"} />
            <Column header="Facility" body={(share: BinderShare) => share.facilityType ? String(share.facilityType).replace(/_/g, " ").toUpperCase() : "Not specified"} />
            <Column header="Shared by" body={(share: BinderShare) => share.ownerDisplayName || share.ownerEmail || "Hub user"} />
            <Column header="Folders" body={(share: BinderShare) => `${share.folderIds?.length || "All shareable"}`} />
            <Column header="Expires" body={(share: BinderShare) => formatDateTime(share.expirationDate)} />
            <Column header="Action" body={(share: BinderShare) => (
              <Button
                label="Open Binder"
                icon="pi pi-folder-open"
                className="p-button-outlined p-button-sm"
                onClick={() => share.shareId && navigate(RouterAddress.getSharedBinderPagePath(share.shareId))}
              />
            )} />
            <Column header="Status" body={() => <Tag value="Active" severity="success" />} />
          </DataTable>}
        </div> : <SharedContentEmptyState type="sharing" />}
      </Card>
      <Dialog visible={showHiringRequestDialog && !!selectedRequest} modal style={{ width: "48rem", maxWidth: "95vw" }} onHide={() => setShowHiringRequestDialog(false)} header={selectedRequest?.viewerMode === "recipient" ? "Complete Hiring Request" : "Hiring Request Details"}>
        {loadingRequest ? <ProgressSpinner /> : selectedRequest ? <div className="flex flex-column gap-3">
          <div className="surface-50 border-1 surface-border border-round p-3"><div className="font-semibold">{selectedRequest.viewerMode === "recipient" ? "Complete your application" : "Request summary"}</div><div className="text-color-secondary mt-1">Role: {String(selectedRequest.role || "").toUpperCase()} {selectedRequest.facilityType ? ` · ${String(selectedRequest.facilityType).toUpperCase()}` : ""}</div></div>
          <div className="surface-card border-1 surface-border border-round p-3"><div className="font-semibold mb-2">Requested certifications</div><ul className="mt-0 mb-0">{(selectedRequest.certifications || []).map((certification: string) => <li key={certification}>{certification}</li>)}</ul></div>
          {selectedRequest.viewerMode === "recipient" && <div className="surface-50 border-1 surface-border border-round p-3 text-sm"><strong>Next steps:</strong> Create your Binder, upload the requested certification documents, complete the questions below, and share the Binder back with the provider.</div>}
          <div className="flex flex-column gap-3">{(selectedRequest.questions || []).map((item: any, index: number) => { const answerKey = String(item.questionId || item.question || index); return <div key={answerKey} className="surface-card border-1 surface-border border-round p-3"><label htmlFor={`hiring-answer-${answerKey}`} className="font-semibold block mb-2">{index + 1}. {item.question}</label>{selectedRequest.viewerMode === "recipient" ? <InputTextarea id={`hiring-answer-${answerKey}`} value={answerDrafts[answerKey] || ""} onChange={(event) => setAnswerDrafts((current) => ({ ...current, [answerKey]: event.target.value }))} rows={3} autoResize className="w-full" placeholder="Type your answer" /> : <div className="text-color-secondary" style={{ whiteSpace: "pre-wrap" }}>{answerDrafts[answerKey] || "No answer submitted yet."}</div>}</div>; })}</div>
          {selectedRequest.viewerMode === "recipient" ? <div className="flex justify-content-end gap-2"><Button label="Save Only" icon="pi pi-save" className="p-button-outlined" onClick={() => void saveAnswers(false)} disabled={savingRequest} /><Button label="Save Only + Notify Owner" icon="pi pi-send" severity="warning" onClick={() => setShowNotifyOwnerConfirmation(true)} disabled={savingRequest} /></div> : <div className="flex justify-content-end">{selectedRequest.binderShareId && <Button label="Open Shared Binder" icon="pi pi-folder-open" className="p-button-outlined p-button-sm" onClick={() => navigate(RouterAddress.getSharedBinderPagePath(selectedRequest.binderShareId))} />}</div>}
        </div> : null}
      </Dialog>
      <Dialog
        visible={showNotifyOwnerConfirmation}
        modal
        style={{ width: "34rem", maxWidth: "92vw" }}
        header="Share your Binder with the owner"
        onHide={() => !savingRequest && setShowNotifyOwnerConfirmation(false)}
      >
        <div className="flex flex-column gap-3">
          <div className="surface-50 border-1 surface-border border-round p-3">
            <div className="font-semibold">Did you also share your Binder with them?</div>
            <div className="text-color-secondary mt-1">The owner can review your documents only after you share the Binder.</div>
          </div>
          <div>
            <div className="font-semibold mb-2">If you have not shared it yet:</div>
            <ol className="mt-0 mb-0 pl-4">
              <li>Open <strong>Binder</strong> from the Hub.</li>
              <li>Upload the requested documents.</li>
              <li>Click <strong>Share</strong> and select the hiring request owner.</li>
              <li>Return here and notify the owner after submitting your answers.</li>
            </ol>
          </div>
          <div className="flex justify-content-end gap-2">
            <Button label="Go Back" icon="pi pi-arrow-left" className="p-button-text" onClick={() => setShowNotifyOwnerConfirmation(false)} disabled={savingRequest} />
            <Button label="Yes, Notify Owner" icon="pi pi-send" severity="warning" onClick={confirmNotifyOwner} disabled={savingRequest} />
          </div>
        </div>
      </Dialog>
      <HiringRequestDialog visible={showHiringIntakeDialog} onHide={() => setShowHiringIntakeDialog(false)} onSent={() => setHiringRequestsRefreshKey((current) => current + 1)} />
      <Card className="mt-3" style={appSectionCardStyle}>
        <div className="flex justify-content-between align-items-center gap-3 flex-wrap">
          {renderSurfaceHeader("Hiring Requests", "Sent and received hiring requirements.", "pi pi-send")}
          {canCreateHiringRequest && <Button label="New Staff Hiring Form" icon="pi pi-plus" severity="warning" className="p-button-sm" onClick={() => setShowHiringIntakeDialog(true)} />}
        </div>
        {requests.length ? (
          <div className="mt-3" style={appDataTableWrapperStyle}>
            {mobile ? <div className="flex flex-column gap-3">
              {sortedRequests.map((request: any) => (
                <div key={request.requestId} className="surface-card border-1 surface-border border-round p-3">
                  <div className="flex align-items-start justify-content-between gap-3">
                    <div style={{ minWidth: 0 }}>
                      <div className="font-semibold" style={{ overflowWrap: "anywhere" }}>{getHiringRequestEmail(request)}</div>
                      <div className="text-color-secondary mt-1">{request.viewerMode === "recipient" ? "Received" : "Sent"}</div>
                    </div>
                    <Tag value={String(request.status || "").replace(/\b\w/g, (character: string) => character.toUpperCase()) || "Unknown"} severity="info" />
                  </div>
                  <div className="grid mt-2 row-gap-2">
                    <div className="col-6"><div className="text-color-secondary text-sm">Role</div><div className="font-medium">{String(request.role || "").toUpperCase() || "Not specified"}</div></div>
                    <div className="col-6"><div className="text-color-secondary text-sm">Facility</div><div className="font-medium">{String(request.facilityType || "").toUpperCase() || "Not specified"}</div></div>
                  </div>
                  <Button label="Open Request" icon="pi pi-external-link" className="p-button-outlined w-full mt-3" onClick={() => openHiringRequest(request)} />
                </div>
              ))}
            </div> : <DataTable value={sortedRequests} paginator={requests.length > 5} rows={5}>
              <Column header="Email" body={(request: any) => getHiringRequestEmail(request)} />
              <Column header="View" body={(request: any) => request.viewerMode === "recipient" ? "Received" : "Sent"} />
              <Column header="Role" body={(request: any) => String(request.role || "").toUpperCase()} />
              <Column header="Facility" body={(request: any) => String(request.facilityType || "").toUpperCase() || "—"} />
              <Column header="Status" field="status" />
              <Column header="Action" body={(request: any) => <Button label="Open Request" icon="pi pi-external-link" className="p-button-outlined p-button-sm" onClick={() => openHiringRequest(request)} />} />
            </DataTable>}
          </div>
        ) : <SharedContentEmptyState type="hiring" />}
      </Card>
      {authStore?.isAdmin?.() ? <AdminResetControl label="Reset Shared Content" description="Delete the shared Binder access records and hiring requests connected to this administrator account. Binders and uploaded documents are preserved." onReset={resetSharedContent} /> : null}
    </div>
  );
};

export default SharedContentPage;
